/**
 * The one seam where `events-api`'s alert catalog and `notification-api`'s
 * per-fan game settings both reach this feature.
 *
 * There is no optimistic local write: `set-game-alert` invalidates
 * `get-game` on success, so the row settles to the server's value on its own.
 * On failure the cache is untouched, so the row is already back at its
 * pre-tap value the moment `isRowPending` clears.
 *
 * A saved alert also invalidates the All feed, whose order puts games
 * with alerts on first (ISSUE-1197), and the league day and week reads,
 * whose rows fill the bell when alerts are on (ISSUE-1391).
 *
 * There is no follow call and no `isFollowing` read here: `set-game-alert`
 * has no follow precondition, so a flip is exactly one round trip.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEventAlertKindsGet } from "@sportsmet/events-sdk/private";
import {
  useNotificationSettingsGetGame,
  useNotificationSettingsSetGameAlert,
} from "@sportsmet/notification-sdk/private";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  buildGameAlertRows,
  resolveGameAlertsViewState,
  type typeGameAlertRow,
  type typeGameAlertsViewState,
} from "./gameAlertsLogic";

/** Every /feed/upcoming query, whatever its payload (packages/events-sdk useFeedUpcoming key). */
export const FEED_UPCOMING_QUERY_KEY_PREFIX: readonly string[] = [
  "post:feed:feedUpcoming",
  "svc:events-api",
];

/** Every /event/list and /event/week-list query (ISSUE-1391): their rows
 *  carry `hasAlertsOn` for the filled bell. */
export const EVENT_LIST_QUERY_KEY_PREFIXES: readonly (readonly string[])[] = [
  ["post:event:eventList", "svc:events-api"],
  ["post:event:eventWeekList", "svc:events-api"],
];

export type typeGameAlertsData = {
  viewState: typeGameAlertsViewState;
  rows: typeGameAlertRow[];
  isRowPending: (alertKindKey: string) => boolean;
  toggleAlert: (alertKindKey: string, next: boolean) => void;
};

function extractErrorCode(query: {
  data?: { error?: { code?: string | null } | null } | undefined;
  isError: boolean;
}): string | null {
  const code = query.data?.error?.code;
  if (typeof code === "string" && code.length > 0) return code;
  if (query.isError) return "OFFLINE";
  return null;
}

export function useGameAlerts(eventId: string): typeGameAlertsData {
  const queryClient = useQueryClient();
  const catalog = useEventAlertKindsGet({ eventId });
  const settings = useNotificationSettingsGetGame({ eventId });
  const setGameAlert = useNotificationSettingsSetGameAlert();

  const viewState = resolveGameAlertsViewState({
    isCatalogPending: catalog.isPending,
    isSettingsPending: settings.isPending,
    catalogErrorCode: extractErrorCode(catalog),
    settingsErrorCode: extractErrorCode(settings),
  });

  const rows =
    viewState === "ready"
      ? buildGameAlertRows({
          kinds: catalog.data?.data?.kinds ?? [],
          alerts: settings.data?.data?.alerts ?? [],
        })
      : [];

  const isRowPending = (alertKindKey: string): boolean =>
    setGameAlert.isPending &&
    setGameAlert.variables.alertKindKey === alertKindKey;

  const toggleAlert = (alertKindKey: string, next: boolean): void => {
    setGameAlert.mutate(
      { eventId, alertKindKey, isEnabled: next },
      {
        onSuccess: (result) => {
          showApiError(result);
          if (result.error == null) {
            void queryClient.invalidateQueries({
              queryKey: [...FEED_UPCOMING_QUERY_KEY_PREFIX],
            });
            for (const prefix of EVENT_LIST_QUERY_KEY_PREFIXES) {
              void queryClient.invalidateQueries({ queryKey: [...prefix] });
            }
          }
        },
        onError: () => {
          showOfflineError();
        },
      },
    );
  };

  return { viewState, rows, isRowPending, toggleAlert };
}
